<template>
    <div class="table-responsive">
        <loader v-if="isLoading" />
        <b-table-simple v-else borderless hover class="list">
            <b-thead>
                <b-tr>
                    <b-th>
                        <b-form-checkbox
                            v-if="isDeleteMultiple"
                            v-model="selectAll"
                            @change="toggleAll"
                            plain
                        >
                        </b-form-checkbox
                    ></b-th>
                    <b-th>Item:</b-th>
                    <b-th>Patient:</b-th>
                    <b-th>Provider:</b-th>
                    <b-th>Quantity:</b-th>
                    <b-th>Price:</b-th>
                    <b-th>Discount:</b-th>
                    <b-th>Tax:</b-th>
                    <b-th>Line Total:</b-th>
                    <b-th>Print:</b-th>
                    <b-th></b-th>
                </b-tr>
            </b-thead>
            <b-tbody v-if="invoiceItems.length == 0">
                <b-tr>
                    <td colspan="10">
                        <i>No records available</i>
                    </td>
                </b-tr>
            </b-tbody>
            <draggable
                v-else
                v-model="invoiceItems"
                tag="tbody"
                ghost-class="sort-row-ghost"
                drag-class="sort-row-drag"
                :animation="0"
                handle=".handle"
                @start="isRowDragging = true"
                @end="isRowDragging = false"
                @sort="itemSorted"
            >
                <tr v-for="invoiceItem in invoiceItems" :key="invoiceItem.id">
                    <td :class="{ handle: !isDeleteMultiple }">
                        <span v-if="isDeleteMultiple">
                            <b-form-checkbox
                                v-if="
                                    'deleteItem' in invoiceItem &&
                                    _hasAccess('billing.invoice.item.delete') &&
                                    invoiceItem.canDeleteItem
                                "
                                v-model="invoiceItem.deleteItem"
                                @change="checkboxChange"
                                plain
                            >
                            </b-form-checkbox>
                        </span>
                        <span
                            v-else
                            :id="`item_${invoiceItem.id}`"
                            class="cus-black-white-swatch cursor-move disable-touch-select"
                        ></span>
                    </td>
                    <td>
                        <div class="d-flex flex-column">
                            <div class="d-flex align-items-center">
                                <item-name-row
                                    v-if="invoiceItem.item"
                                    :invoiceItem="invoiceItem"
                                    :name="invoiceItem.item_name"
                                    :isNewLabProfile="isNewLab"
                                    @view="viewRecord"
                                    @edit="editRecord"
                                    :disabled="isHasUsersChooseInvoice"
                                >
                                </item-name-row>
                                <i
                                    v-if="shouldShowAuditIcon(invoiceItem)"
                                    @click="toggleAuditTimeline(invoiceItem)"
                                    class="fas fa-info-circle cursor-pointer text-info ml-2"
                                ></i>
                            </div>
                            <audit-summary
                                v-if="
                                    shouldShowAuditIcon(invoiceItem) &&
                                    selectedAuditItem === invoiceItem.id
                                "
                                :invoiceItem="invoiceItem"
                                @close="selectedAuditItem = null"
                            />
                        </div>
                    </td>
                    <td>
                        <patient-name
                            v-if="invoiceItem.patient"
                            :patient="invoiceItem.patient"
                        />
                    </td>
                    <td>
                        <span>
                            {{
                                invoiceItem.provider
                                    ? invoiceItem.provider.display_name
                                    : invoiceItem.default_provider &&
                                      invoiceItem.prescribed_by ==
                                          invoiceItem.default_provider.id
                                    ? invoiceItem.default_provider.display_name
                                    : ""
                            }}
                        </span>
                    </td>
                    <td>
                        {{ invoiceItem.quantity }}
                        {{
                            invoiceItem.item_uom
                                ? invoiceItem.item_uom.value
                                : ""
                        }}
                    </td>
                    <td class="text-nowrap">
                        <span
                            v-if="
                                invoiceItem.item &&
                                invoiceItem.item.type !=
                                    CITEMTYPES.TYPES.TYPE_DISCOUNT
                            "
                        >
                            {{ displayPrice(invoiceItem) }}
                        </span>
                    </td>
                    <td class="text-nowrap">
                        {{ displayDiscount(invoiceItem.discount) }}
                    </td>
                    <td>
                        <span
                            v-if="
                                invoiceItem.item &&
                                invoiceItem.item.type !=
                                    CITEMTYPES.TYPES.TYPE_DISCOUNT
                            "
                        >
                            {{
                                _toCurrency(
                                    invoiceItem.tax ? invoiceItem.tax : 0
                                )
                            }}
                        </span>
                    </td>
                    <td>{{ displayLineTotal(invoiceItem) }}</td>
                    <td>
                        {{ invoiceItem.print_on_invoice == 1 ? "Yes" : "No" }}
                    </td>
                    <td class="text-right">
                        <span
                            v-if="
                                invoice.isBusinessActorActive &&
                                invoiceItem.item
                            "
                        >
                            <!-- FOR INVOICE ITEM TYPE OTHER THAN DISCOUNT AND VETSOURCE -->
                            <b-dropdown
                                variant="link"
                                toggle-class="text-decoration-none"
                                no-caret
                                right
                                @show="canProceedEdit()"
                                v-if="
                                    invoiceItem.item.type !=
                                        CITEMTYPES.TYPES.TYPE_DISCOUNT &&
                                    (invoiceItem.canEditItem ||
                                        invoiceItem.canDeclineItem ||
                                        invoiceItem.canDeleteItem) &&
                                    !isVetsourceItem(invoiceItem.item)
                                "
                                title="Additional options"
                            >
                                <template v-slot:button-content>
                                    <i class="cus-cog-down"></i>
                                </template>

                                <b-dropdown-item
                                    v-if="invoiceItem.canEditItem"
                                    href="#"
                                    @click="editItem(invoiceItem)"
                                    :disabled="isHasUsersChooseInvoice"
                                >
                                    <i class="cus-page-white-edit"></i>
                                    Edit Item
                                </b-dropdown-item>
                                <b-dropdown-item
                                    v-if="isCanCreate"
                                    href="#"
                                    @click="duplicateItem(invoiceItem)"
                                    :disabled="
                                        isHasUsersChooseInvoice ||
                                        isCubexItem(invoiceItem.record)
                                    "
                                    v-b-tooltip.hover.top
                                    :title="
                                        isCubexItem(invoiceItem.record)
                                            ? 'This functionality is not currently available when the Cubex Integration is enabled'
                                            : ''
                                    "
                                >
                                    <i class="cus-page-copy"></i>
                                    Duplicate Item
                                </b-dropdown-item>
                                <b-dropdown-item
                                    href="#"
                                    v-if="invoiceItem.canDeclineItem"
                                    @click="declinedItem(invoiceItem)"
                                    :disabled="
                                        isHasUsersChooseInvoice ||
                                        isCubexItem(invoiceItem.record)
                                    "
                                    v-b-tooltip.hover.top
                                    :title="
                                        isCubexItem(invoiceItem.record)
                                            ? 'This functionality is not currently available when the Cubex Integration is enabled'
                                            : ''
                                    "
                                >
                                    <i class="cus-page-delete"></i>
                                    Decline Item
                                </b-dropdown-item>
                                <b-dropdown-item
                                    href="#"
                                    v-if="invoiceItem.canDeleteItem"
                                    @click="deleteItem(invoiceItem)"
                                    :disabled="isHasUsersChooseInvoice"
                                >
                                    <i class="cus-delete"></i>
                                    Delete Item
                                </b-dropdown-item>
                            </b-dropdown>

                            <!-- FOR INVOICE ITEM TYPE DISCOUNT OR VETSOURCE ITEM -->
                            <i
                                v-else-if="
                                    (invoiceItem.item.type ==
                                        CITEMTYPES.TYPES.TYPE_DISCOUNT ||
                                        isVetsourceItem(invoiceItem.item)) &&
                                    invoiceItem.canDeleteItem
                                "
                                @click="deleteItem(invoiceItem)"
                                class="cus-delete cursor-pointer"
                                v-b-tooltip.hover.top
                                title="Delete Item"
                                :class="
                                    isHasUsersChooseInvoice
                                        ? 'disabled cursor-not-allowed'
                                        : 'cursor-pointer'
                                "
                            ></i>
                        </span>
                    </td>
                </tr>
            </draggable>
        </b-table-simple>
        <modal-record
            ref="modalRecord"
            @onSuccess="reloadRelated"
            page="invoice"
        />
        <modal-view-record ref="modalViewRecord" />
        <modal-decline-item
            ref="modalDeclineItem"
            modalRef="modalDeclineItem"
            :item="item"
            @reload="reloadRelated"
        />
        <modal-delete-invoice-item
            ref="modalDeleteInvoiceItem"
            modalRef="modalDeleteInvoiceItem"
            :invoice="invoice"
            @reload="reloadRelated"
        />

        <modal-delete-in-bulk
            ref="modalBulkDelete"
            @reload="reloadRelated"
            :invoiceItems="invoiceItems"
        />
        <invoiceItemModal
            ref="invoiceItemModal"
            :invoice="invoice"
            @reload="reloadRelated"
            @redirect-to-dashboard="redirectToDashboard"
        />
    </div>
</template>

<script>
/**
 * @name InvoiceItemList
 * @description List of Invoice Item
 * @see apps/application/views/staff/billing/invoice/invoice/sub/item/list.php
 * @see apps/application/controllers/billing/item.php:18 or function all()
 */
import { forEach, filter, round, isEmpty } from "lodash";
import InvoiceItemService from "@/services/invoice/invoice-item";
import CITEMTYPES from "@/constants/item-types";
import CBILLINGS from "@/constants/billings";
import invoiceHelper from "@/mixins/invoice";
import AccountService from "@/services/account.js";
import EXTENSIONS from "@/constants/extensions";
import CubexMixin from "@/mixins/cubex";
import RecordRowMixin from "@/mixins/record-row";

const STATUS_DECLINED = 1;
const CUBEX_DISPENSED = "Dispensed";

export default {
    mixins: [invoiceHelper, CubexMixin, RecordRowMixin],
    components: {
        Loader: () => import("@/components/elements/loader-list"),
        PatientName: () => import("@/components/commons/patient-name"),
        draggable: () => import("vuedraggable"),
        ModalRecord: () => import("@/module/commons/record/create-update"),
        ItemNameRow: () => import("@/components/item/item-name-row"),
        ModalViewRecord: () => import("@/components/medical-records/view"),
        ModalDeclineItem: () =>
            import("@/components/invoice-item/item-decline-modal"),
        ModalDeleteInvoiceItem: () =>
            import("@/components/invoice-item/modal-delete-invoice-item"),
        ModalDeleteInBulk: () =>
            import(
                "@/components/invoice-item/modal-bulk-delete-invoice-items.vue"
            ),
        invoiceItemModal: () =>
            import("@/components/invoice-item/create-update"),
        AuditSummary: () => import("@/components/invoice-item/audit-summary")
    },
    props: {
        invoice: {
            type: Object,
            required: true
        },
        isDeleteMultiple: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        isCanCreate() {
            return (
                !this.invoice.isPaid &&
                this.invoice.isBusinessActorActive &&
                this._hasAccess("billing.invoice.item.create") &&
                this.invoice.status != CBILLINGS.INVOICE.TYPE_LOCKED
            );
        },
        isHasUsersChooseInvoice() {
            return !isEmpty(
                this.$store.getters["billing/findUserChooseInvoice"](
                    this.invoice.id
                )
            );
        }
    },
    watch: {
        isDeleteMultiple() {
            this.invoiceItems = forEach(this.invoiceItems, item => {
                if (!item.is_billing_lock) item.deleteItem = false;
            });
            this.checkboxChange();
        }
    },
    data() {
        return {
            STATUS_DECLINED: STATUS_DECLINED,
            CITEMTYPES: CITEMTYPES,
            CBILLINGS: CBILLINGS,
            isLoading: false,
            showPagination: false,
            invoiceItems: [],
            selectedItem: null,
            isRowDragging: false,
            item: {},
            selectAll: false,
            isNewLab: false,
            selectedAuditItem: null
        };
    },
    mounted() {
        // to disable the menu when the user long press on the icon
        $(document).on("contextmenu", ".handle", event => {
            event.preventDefault();
            event.stopPropagation();
            return false;
        });

        this.$root.$on("antech-status-update", this.getInvoiceItems);

        this.getInvoiceItems();
        this.isNewLabProfile();
    },
    destroyed() {
        $(document).off("contextmenu", ".handle");
        this.$root.$off("antech-status-update", this.getInvoiceItems);
    },
    methods: {
        getInvoiceItems() {
            let self = this;
            this.isLoading = true;
            this.selectAll = false;

            InvoiceItemService.getInvoiceItems(this.invoice.id).then(res => {
                self.isLoading = false;
                self.invoiceItems = forEach(res, iitem => {
                    if (!iitem.is_billing_lock) {
                        iitem.deleteItem = false;
                    }

                    iitem.canEditItem = false;
                    iitem.canDeclineItem = false;

                    if (iitem.item.type != CITEMTYPES.TYPES.TYPE_DISCOUNT) {
                        iitem.canEditItem =
                            this._hasAccess("billing.invoice.item.update") &&
                            (!this.invoice.isPaid ||
                                this.invoice.status ==
                                    CBILLINGS.INVOICE.TYPE_REOPENED);

                        iitem.canDeclineItem =
                            !iitem.hasDailyRateItem &&
                            this._hasAccess("billing.invoice.item.decline") &&
                            !this.invoice.isPaid &&
                            this.invoice.status !=
                                CBILLINGS.INVOICE.TYPE_REOPENED;

                        iitem.canDeleteItem =
                            !iitem.hasDailyRateItem &&
                            this._hasAccess("billing.invoice.item.delete") &&
                            (!this.invoice.isPaid ||
                                this.invoice.status ==
                                    CBILLINGS.INVOICE.TYPE_REOPENED);
                    } else {
                        iitem.canDeleteItem =
                            this._hasAccess("billing.invoice.item.delete") &&
                            (!this.invoice.isPaid ||
                                this.invoice.status ==
                                    CBILLINGS.INVOICE.TYPE_REOPENED);
                    }
                });
                self.checkboxChange();
            });
        },
        isVetsourceItem(item) {
            if (item && item.master_item && item.master_item.source) {
                return item.master_item.source === "VETSOURCE";
            }
            return false;
        },
        shouldShowAuditIcon(invoiceItem) {
            if (
                !invoiceItem.item_dispensing_references ||
                !invoiceItem.item_dispensing_references[0]
            ) {
                return false;
            }

            // Check if dispensing references exist and have audits
            return (
                invoiceItem.item_dispensing_references[0].audits &&
                invoiceItem.item_dispensing_references[0].audits.length > 0 &&
                invoiceItem.item_dispensing_references[0].dispensed !=
                    invoiceItem.item_dispensing_references[0].quantity &&
                this.invoice.cubex_show_label
            );
        },
        async toggleAll(checked) {
            forEach(this.invoiceItems, function (iitem) {
                if (iitem.canDeleteItem) {
                    iitem.deleteItem = checked;
                }
            });

            this.checkboxChange();
        },
        checkboxChange() {
            this.$nextTick(() => {
                let listSelected = filter(this.invoiceItems, "deleteItem");
                this.selectAll =
                    listSelected.length ==
                    filter(this.invoiceItems, { canDeleteItem: true }).length;
                this.$emit("is-item-selected", listSelected.length);
            });
        },
        bulkDelete() {
            this.$refs.modalBulkDelete.open();
        },
        displayDiscount(invoiceItemDiscount = null) {
            let discount = 0;
            if (invoiceItemDiscount) {
                discount = round(invoiceItemDiscount, 2);
            }

            return this._toCurrency(discount);
        },
        displayPrice(invoiceItem) {
            // To handle the floating point number precision
            // Ex: 0.1 + 0.2 = 0.3000000000000001
            // The round(x,2) code will eliminitate the decimals
            // for issue VET-1340 and VET-1428
            let price = 0;
            if (invoiceItem.print_on_invoice == 1 && invoiceItem.quantity > 0) {
                price = round(invoiceItem.price + invoiceItem.fee, 2);
            }
            return this._toCurrency(price);
        },
        displayLineTotal(invoiceItem) {
            let amount = 0;
            let discount = 0;
            if (invoiceItem.discount > 0) {
                if (
                    invoiceItem.discount_type ==
                    CITEMTYPES.DISCOUNT_TYPES.DISCOUNT_TYPE_PERCENTAGE
                ) {
                    discount =
                        round(invoiceItem.price + invoiceItem.fee, 2) *
                        round(invoiceItem.discount / 100, 2);
                } else {
                    discount = invoiceItem.discount;
                }
            }

            if (invoiceItem.print_on_invoice == 1) {
                amount =
                    round(
                        invoiceItem.quantity > 0
                            ? invoiceItem.price + invoiceItem.fee
                            : 0,
                        2,
                        2
                    ) -
                    round(discount, 2) +
                    invoiceItem.tax;
            } else {
                amount = 0;
            }
            return this._toCurrency(amount);
        },
        viewRecord(recordDetails) {
            this.$refs.modalViewRecord.open(recordDetails);
        },
        editRecord(recordDetails) {
            this.$refs.modalRecord.open(recordDetails);
        },
        editItem(invoiceItem) {
            this.$refs["invoiceItemModal"].open(invoiceItem);
        },
        duplicateItem(invoiceItem) {
            this.$refs["invoiceItemModal"].open(invoiceItem, true);
        },
        declinedItem(invoiceItem) {
            this.item = invoiceItem;
            let date = this.invoice.is_editable
                ? this._fromDateCarbon(invoiceItem.date).toFormat("X")
                : this._fromDateCarbon(this.invoice.start).toFormat("X");

            this.$store.dispatch("checkBillingLock", {
                date,
                onProceed: () => this.$refs.modalDeclineItem.open(),
                onError: message => this._message(message, "danger")
            });
        },
        deleteItem(invoiceItem) {
            if (this.isHasUsersChooseInvoice) {
                return;
            }
            this.item = invoiceItem;
            let date = this.invoice.is_editable
                ? this._fromDateCarbon(invoiceItem.date).toFormat("X")
                : this._fromDateCarbon(this.invoice.start).toFormat("X");

            this.$store.dispatch("checkBillingLock", {
                date,
                onProceed: () =>
                    this.$refs.modalDeleteInvoiceItem.open(invoiceItem),
                onError: message => this._message(message, "danger")
            });
        },
        reloadRelated() {
            this.getInvoiceItems();
            this.$emit("delete-multiple", false);
            this.$emit("reload-invoice-details", null, [1, 5]); // null to prevent the item list component rerendered and the array is to reload item and decline tab count
        },
        async itemSorted() {
            const params = {
                id: this.invoice.id,
                status: 1,
                data: this.invoiceItems.map(invItem => invItem.id)
            };

            await InvoiceItemService.updateInvoiceItemOrder(params)
                .then(res => {
                    if (res.code != 200) {
                        throw "Error: Cannot reorder invoice item";
                    }
                })
                .catch(error => {
                    this._message(error, "danger");
                });
        },
        async canProceedEdit() {
            let proceed = await this.checkInvoiceStatus(this.invoice.id);
            if (!proceed) {
                this.reloadRelated();
                return proceed;
            }
        },
        redirectToDashboard() {
            this.$emit("redirect-to-dashboard");
        },
        async isNewLabProfile() {
            let res = await AccountService.checkFeature("lab_profile");
            let isEnabled = res?.message?.code;

            // if return 404 -> mean clinic not in beta release table -> redirect to view
            // if return 200 -> mean clinic in beta release table -> redirect to apps
            if (isEnabled === 404) {
                this.isNewLab = true;
            }
        },
        toggleAuditTimeline(invoiceItem) {
            if (this.selectedAuditItem === invoiceItem.id) {
                this.selectedAuditItem = null;
            } else {
                this.selectedAuditItem = invoiceItem.id;
            }
        }
    }
};
</script>
